/**
 * masterDataStore — persistent, device-local cache of the master collections
 * that every transaction form + POS re-read on each open (inventoryItems,
 * customers, vendors, accounts). This was the #1 Firestore read leak: those
 * full-collection reads ran on every screen mount, uncached (e.g. Gota =
 * ~1,200 reads per Non-Tax-Invoice open × ~160 opens/day).
 *
 * HOW IT WORKS (safe with the app's messy data):
 *  - Reads come from IndexedDB (Dexie) → 0 Firestore reads per form open.
 *  - A throttled CHANGE-CHECK (not a naive delta) decides when to refetch:
 *      count-of-collection (1 read) catches adds/deletes,
 *      newest updatedAt   (1 read) catches in-place edits.
 *    Only when one of those moves do we refetch the whole (small) collection.
 *  - Why change-check and not delta sync: customers/vendors/accounts are
 *    HARD-deleted (a delta `updatedAt >` query can never see a deleted row →
 *    ghost rows), and their updatedAt is client-clock (skew). Count + newest
 *    is robust to both. Items are cleaner (soft-delete + serverTimestamp) but
 *    use the same path for uniformity.
 *  - Cold-start backstop: the first check per browser session forces a full
 *    refetch, so every device is guaranteed fully correct at least once/session.
 *  - Local edits write straight into IndexedDB (write-through) so the editing
 *    device is always instant + correct without waiting for a refetch.
 *
 * Compromise (owner-approved 27 Jul): another device may see a new/edited
 * item/customer/price up to the throttle window (~5 min) late; same-device
 * edits are instant. Prices used on invoices come from this cache, so the
 * change-check + session backstop keep drift bounded.
 */

import {
  collection, getDocs, query, orderBy, limit, getCountFromServer, where, Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { dexieDb } from '../dataStore/dexieSchema';
import { cacheCollection, readCollection } from './posLocalCache';
import { recordReads } from './readTelemetry';

// Firestore collection → Dexie table. (updatedAt is the change signal.)
const MAP = {
  inventoryItems: 'inventory_items',
  customers: 'customers',
  vendors: 'vendors',
  accounts: 'accounts',
  // Added 2026-08-04: these three were read raw on EVERY mount of ~40 screens
  // (~110 reads per page open) because no cache tier existed for them.
  employees: 'employees',
  jobs: 'jobs',
  inventoryLocations: 'inventory_locations',
};

// Small collections: cheap enough to re-pull whole on a timer, which also
// covers them not having a reliable `updatedAt` on every row.
const SMALL_COLLS = new Set(['employees', 'jobs', 'inventoryLocations']);
const SMALL_MAX_AGE_MS = 30 * 60 * 1000;

const CHECK_THROTTLE_MS = 5 * 60 * 1000; // change-check at most every 5 min/coll/company/device
const _lastCheck = new Map();  // `${companyId}|${coll}` -> ts of last change-check
const _inFlight = new Map();   // `${companyId}|${coll}` -> Promise (dedupe concurrent syncs)

const metaKey = (companyId, coll) => `master|${companyId}|${coll}`;

// ── Refresh policy (owner decision 2026-08-04) ──────────────────────────────
// The cache lives in IndexedDB and SURVIVES tab close, new tabs and normal
// navigation — a new tab now costs 0 reads instead of re-downloading ~3,800
// docs (the old backstop was keyed to sessionStorage, i.e. once per TAB).
// A full re-download happens when the user RELOADS the page (F5 / reload
// button). Browsers report F5 and Ctrl+F5 identically, so any reload is the
// trigger — the staff instruction is simply "press F5 to pull fresh data".
const IS_PAGE_RELOAD = (() => {
  try {
    const nav = performance.getEntriesByType('navigation')[0];
    if (nav && typeof nav.type === 'string') return nav.type === 'reload';
    // Legacy fallback (older Safari): 1 === TYPE_RELOAD
    return performance.navigation ? performance.navigation.type === 1 : false;
  } catch { return false; }
})();
// Module scope ⇒ resets on every page load, so each reload forces exactly one
// full refresh per collection, not one per component mount.
const _reloadSynced = new Set();

// Normalize any updatedAt (Firestore Timestamp OR ISO string OR millis) to a
// comparable number, so items (Timestamp) and customers (string) both work.
const normTs = (v) => {
  if (v == null) return 0;
  if (typeof v === 'number') return v;
  if (typeof v === 'string') { const t = Date.parse(v); return Number.isNaN(t) ? 0 : t; }
  if (typeof v.toMillis === 'function') { try { return v.toMillis(); } catch { return 0; } }
  if (typeof v.seconds === 'number') return v.seconds * 1000;
  return 0;
};

const readMeta = async (companyId, coll) => {
  try {
    const row = await dexieDb.sync_state.get(metaKey(companyId, coll));
    return row || null;
  } catch { return null; }
};
const writeMeta = async (companyId, coll, count, maxUpdated) => {
  try {
    await dexieDb.sync_state.put({
      collectionName: metaKey(companyId, coll),
      lastPulledAt: new Date().toISOString(),
      count, maxUpdated,
    });
  } catch { /* meta is best-effort */ }
};

// Full refetch of one collection → replace the Dexie table for this company.
const fullSync = async (companyId, coll) => {
  const ref = collection(db, 'companies', companyId, coll);
  const snap = await getDocs(ref);
  recordReads(companyId, snap.size, 'master-sync');
  const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  await cacheCollection(MAP[coll], companyId, rows);
  let maxUpdated = 0;
  for (const r of rows) { const t = normTs(r.updatedAt); if (t > maxUpdated) maxUpdated = t; }
  await writeMeta(companyId, coll, rows.length, maxUpdated);
  return rows;
};

// Delta refetch — inventoryItems ONLY. Safe there because items are
// soft-deleted (a delete still appears in the delta as an edited row) —
// unlike customers/vendors/accounts, which are hard-deleted and must keep
// the full-refetch path. Why this exists: every stock-in save bumps
// updatedAt on its items (cost snapshot), which tripped the 5-min change
// check on EVERY active device into a full collection re-download all day
// (/master-sync measured as the top read screen for several companies).
// Fetching just the changed rows keeps the same ≤5-min cost freshness at
// a handful of reads instead of the whole item list.
// NOTE: updatedAt holds MIXED types (serverTimestamp on normal edits, ISO
// string from updateItemCosts) and a Firestore range query only matches its
// own value type — so we must query both types and merge.
const deltaSyncItems = async (companyId, sinceMs, serverCount) => {
  const coll = 'inventoryItems';
  const ref = collection(db, 'companies', companyId, coll);
  const boundary = Math.max(0, sinceMs - 1); // re-include boundary-ms rows; merge is idempotent
  const [strSnap, tsSnap] = await Promise.all([
    getDocs(query(ref, where('updatedAt', '>', new Date(boundary).toISOString()))),
    getDocs(query(ref, where('updatedAt', '>', Timestamp.fromMillis(boundary)))),
  ]);
  recordReads(companyId, strSnap.size + tsSnap.size, 'master-sync');
  const changed = new Map();
  [...strSnap.docs, ...tsSnap.docs].forEach((d) => changed.set(d.id, { id: d.id, ...d.data() }));
  let maxUpdated = sinceMs;
  for (const row of changed.values()) {
    await dexieDb[MAP[coll]].put({ ...row, _companyId: companyId });
    const t = normTs(row.updatedAt);
    if (t > maxUpdated) maxUpdated = t;
  }
  // Reconcile: if the merged local count doesn't match the server, something
  // slipped past the delta (e.g. an unexpected hard delete) → full refetch.
  const local = await readCollection(MAP[coll], companyId);
  if (typeof serverCount === 'number' && local.length !== serverCount) {
    await fullSync(companyId, coll);
    return;
  }
  await writeMeta(companyId, coll, local.length, maxUpdated);
};

// Decide whether the local copy is stale and refetch if so. Throttled.
// forceFull = the cold-start / post-edit backstop (skip the cheap check).
const maybeSync = async (companyId, coll, { forceFull = false } = {}) => {
  const k = metaKey(companyId, coll);
  if (_inFlight.has(k)) return _inFlight.get(k);

  const run = (async () => {
    try {
      // One full refresh per collection per PAGE LOAD, but only when the page
      // was reloaded (see IS_PAGE_RELOAD). A new tab or an in-app navigation
      // reuses IndexedDB at zero reads.
      let reloadForced = false;
      if (IS_PAGE_RELOAD && !_reloadSynced.has(k)) { reloadForced = true; _reloadSynced.add(k); }

      if (forceFull || reloadForced) { await fullSync(companyId, coll); return; }

      // Throttle the cheap change-check.
      const last = _lastCheck.get(k) || 0;
      if (Date.now() - last < CHECK_THROTTLE_MS) return;
      _lastCheck.set(k, Date.now());

      const meta = await readMeta(companyId, coll);
      if (!meta) { await fullSync(companyId, coll); return; }

      // Small collections (employees / jobs / locations): a few dozen rows, and
      // their rows do not reliably carry `updatedAt`, so an ordering-based check
      // could miss edits. Re-pull whole on a 30-min timer instead — still far
      // cheaper than the old "read them again on every screen mount".
      if (SMALL_COLLS.has(coll)) {
        const age = Date.now() - Date.parse(meta.lastPulledAt || 0);
        if (!(age >= 0) || age > SMALL_MAX_AGE_MS) await fullSync(companyId, coll);
        return;
      }

      const ref = collection(db, 'companies', companyId, coll);
      const cntSnap = await getCountFromServer(ref);
      recordReads(companyId, 1, 'master-sync');
      const serverCount = cntSnap.data().count;

      // ── inventoryItems ────────────────────────────────────────────────────
      // Do NOT use an `orderBy('updatedAt')` probe here. `updatedAt` on items
      // holds TWO Firestore types — an ISO string (written by updateItemCosts
      // after every stock-in) and a Timestamp (written by manual item edits).
      // Firestore orders Timestamp before String, so the probe returns the
      // newest STRING row, never the true newest. That broke the check both
      // ways: permanent 5-minute churn on every device, AND manual item edits
      // staying invisible to other devices. Running the two-type delta directly
      // is the same read cost, always correct, and self-healing.
      if (coll === 'inventoryItems' && meta.maxUpdated) {
        await deltaSyncItems(companyId, meta.maxUpdated, serverCount);
        return;
      }
      if (serverCount !== meta.count) { await fullSync(companyId, coll); return; }

      // count unchanged → check newest updatedAt for in-place edits.
      // (customers / vendors / accounts write ISO strings consistently.)
      const newestSnap = await getDocs(query(ref, orderBy('updatedAt', 'desc'), limit(1)));
      recordReads(companyId, 1, 'master-sync');
      const newest = newestSnap.docs.length ? normTs(newestSnap.docs[0].data().updatedAt) : 0;
      if (newest !== meta.maxUpdated) await fullSync(companyId, coll);
    } catch (e) {
      console.warn(`[masterDataStore] sync ${coll} failed:`, e && e.message);
    } finally {
      _inFlight.delete(k);
    }
  })();

  _inFlight.set(k, run);
  return run;
};

/**
 * Get a master collection for a company. Returns instantly from IndexedDB
 * (0 Firestore reads) and refreshes in the background. On a truly cold cache
 * (nothing local yet) it awaits one full fetch so the caller isn't empty.
 * Callers keep their own client-side .filter()/.sort() on the returned rows.
 */
const getMaster = async (companyId, coll) => {
  if (!companyId || !MAP[coll]) return [];
  let local = [];
  try { local = await readCollection(MAP[coll], companyId); } catch { local = []; }

  if (!local || local.length === 0) {
    // Cold cache — fetch once and return fresh (blocking, first time only).
    try { return await fullSync(companyId, coll); }
    catch (e) {
      // Last-resort direct read so the form is never empty on error.
      try {
        const snap = await getDocs(collection(db, 'companies', companyId, coll));
        recordReads(companyId, snap.size, 'master-sync');
        return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      } catch { return []; }
    }
  }

  // Warm cache — serve local now, refresh in the background (non-blocking).
  maybeSync(companyId, coll);
  return local;
};

// Public API (forms call these instead of raw getDocs).
export const getItemsCached = (companyId) => getMaster(companyId, 'inventoryItems');
export const getCustomersCached = (companyId) => getMaster(companyId, 'customers');
export const getVendorsCached = (companyId) => getMaster(companyId, 'vendors');
export const getAccountsCached = (companyId) => getMaster(companyId, 'accounts');
// Added 2026-08-04. Each of these replaces a raw getDocs that ran on every
// mount of ~40 screens. Callers keep their own isActive/sort filters — these
// return the full stored list, exactly like the four above.
export const getEmployeesCached = (companyId) => getMaster(companyId, 'employees');
export const getJobsCached = (companyId) => getMaster(companyId, 'jobs');
export const getLocationsCached = (companyId) => getMaster(companyId, 'inventoryLocations');

// Areas + Regions are small, rarely-changing masters not in the Dexie cache.
// Loaded once per session, in-memory (0 reads until first needed — e.g. resolving
// a customer's area/region name when printing an invoice — then cached).
const _geoCache = new Map(); // companyId -> { areas, regions }
export const getAreasRegionsCached = async (companyId) => {
  if (!companyId) return { areas: [], regions: [] };
  const hit = _geoCache.get(companyId);
  if (hit) return hit;
  try {
    const [aSnap, rSnap] = await Promise.all([
      getDocs(collection(db, 'companies', companyId, 'areas')),
      getDocs(collection(db, 'companies', companyId, 'regions')),
    ]);
    recordReads(companyId, aSnap.size + rSnap.size, 'geo-print');
    const val = {
      areas: aSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
      regions: rSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
    };
    _geoCache.set(companyId, val);
    return val;
  } catch {
    return { areas: [], regions: [] };
  }
};

// Inventory categories — small master not in the Dexie cache. Session in-memory
// cache, fetched once per session when first needed (e.g. the stock-audit
// category breakup). Returns the full list; callers resolve names by id.
const _catCache = new Map(); // companyId -> categories[]
export const getCategoriesCached = async (companyId) => {
  if (!companyId) return [];
  const hit = _catCache.get(companyId);
  if (hit) return hit;
  try {
    const snap = await getDocs(collection(db, 'companies', companyId, 'inventoryCategories'));
    recordReads(companyId, snap.size, 'categories');
    const val = snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((c) => c.isDeleted !== true);
    _catCache.set(companyId, val);
    return val;
  } catch {
    return [];
  }
};

// Sub-categories and the units list — same session cache as categories
// (Quick Add Item, 2026-10-13).
const _subCatCache = new Map(); // companyId -> subCategories[]
export const getSubCategoriesCached = async (companyId) => {
  if (!companyId) return [];
  const hit = _subCatCache.get(companyId);
  if (hit) return hit;
  try {
    const snap = await getDocs(collection(db, 'companies', companyId, 'inventorySubCategories'));
    recordReads(companyId, snap.size, 'subCategories');
    const val = snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((c) => c.isDeleted !== true);
    _subCatCache.set(companyId, val);
    return val;
  } catch {
    return [];
  }
};
const _unitsCache = new Map(); // companyId -> units[]
export const getUnitsBankCached = async (companyId) => {
  if (!companyId) return [];
  const hit = _unitsCache.get(companyId);
  if (hit) return hit;
  try {
    const snap = await getDocs(collection(db, 'companies', companyId, 'units'));
    recordReads(companyId, snap.size, 'units');
    const val = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    _unitsCache.set(companyId, val);
    return val;
  } catch {
    return [];
  }
};
/** A category or sub-category was added, changed or removed: read them fresh next time. */
export const clearCategoryCaches = (companyId) => {
  _catCache.delete(companyId);
  _subCatCache.delete(companyId);
};

/**
 * Write-through: call after a local create/update of a master row so the
 * editing device is instantly correct (no wait for the next change-check).
 */
export const upsertLocalMaster = async (companyId, coll, row) => {
  if (!companyId || !MAP[coll] || !row?.id) return;
  try {
    await dexieDb[MAP[coll]].put({ ...row, _companyId: companyId });
    // Force the next background check to refetch, so counts/meta reconcile.
    _lastCheck.delete(metaKey(companyId, coll));
  } catch { /* best-effort */ }
};

/**
 * Refresh the local cache for a collection NOW (fire-and-forget). Call after a
 * master create/update/delete so forms opened moments later see the change on
 * the same device, and so counts/meta reconcile. Costs 1 full read of that
 * (small) collection — master edits are infrequent, so this is negligible.
 */
export const invalidateMaster = (companyId, coll) => {
  if (!companyId || !MAP[coll]) return;
  _lastCheck.delete(metaKey(companyId, coll));
  _reloadSynced.delete(metaKey(companyId, coll));
  maybeSync(companyId, coll, { forceFull: true }).catch(() => {});
};

export const COLL = {
  items: 'inventoryItems', customers: 'customers', vendors: 'vendors', accounts: 'accounts',
  employees: 'employees', jobs: 'jobs', locations: 'inventoryLocations',
};
